import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, ArrowRightIcon, CalendarDaysIcon, ClockIcon, MessageSquareIcon, TrendingUpIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, Metric, SectionHeading } from '../../components/ui/Panels';
import { Badge } from '../../components/ui/Badge';
import { ButtonLink } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { BookingCard } from '../../components/bookings/BookingCard';
import { useSession } from '../../contexts/SessionContext';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { counterpartById } from '../../data/counterparts';
import { chefEarnings } from '../../data/stats';
import { currency, longDate, relativeTime, responseTime } from '../../utils/format';
export function Dashboard() {
  const {
    chef
  } = useSession();
  const {
    bookings,
    threads
  } = useAppData();
  const {
    locale,
    currencyCode
  } = useLocale();
  const mine = bookings.filter(b => b.chefId === chef.id);
  const requests = mine.filter(b => b.status === 'requested');
  const confirmed = mine.filter(b => b.status === 'confirmed').sort((a, b) => a.date.localeCompare(b.date));
  const month = chefEarnings[chefEarnings.length - 1];
  const previous = chefEarnings[chefEarnings.length - 2];
  const delta = Math.round((month.amount - previous.amount) / previous.amount * 100);
  const needsReply = threads.filter(t => t.chefId === chef.id && t.unreadForChef > 0).sort((a, b) => b.lastAt.localeCompare(a.lastAt));
  const incomplete = chef.verifications.filter(v => v.status !== 'verified');
  return <AppShell title={`Good evening, ${chef.name.split(' ')[0]}`} subtitle={`Next free date ${longDate(chef.nextAvailable, locale)}`} actions={<ButtonLink to="/chef/studio/menus" variant="outline" size="sm" data-id="element-1006" data-mp="1fveuy8rjlngm">
          Edit my listing
        </ButtonLink>} data-id="element-1005" data-mp="1ktyiuepp59bm">
      {!!requests.length && <Card className="border-action/40 bg-accent-soft p-5" data-id="element-1007" data-mp="3f22d777f9ch1">
          <div className="flex flex-wrap items-center gap-4" data-id="element-1008" data-mp="3f3na33h7l7i2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-action text-on-action" aria-hidden="true" data-id="element-1009" data-mp="30ehavfidfha6">
              <ClockIcon className="h-5 w-5" data-id="element-1010" data-mp="359o8c5l1rpqn" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1011" data-mp="3t2wkfmiqh7e4">
              <p className="font-display text-lg font-semibold text-on-accent" data-id="element-1012" data-mp="1vabbed8wcpv">
                {requests.length} {requests.length === 1 ? 'request is' : 'requests are'} waiting on a quote
              </p>
              <p className="mt-0.5 text-[13px] text-on-accent/80" data-id="element-1013" data-mp="3o28nylbqjyci">
                Guests see your median reply time of {responseTime(chef.responseMinutes)} — it is the single biggest thing
                that wins bookings.
              </p>
            </div>
            <ButtonLink to="/chef/inbox" variant="action" size="sm" data-id="element-1014" data-mp="2comzz3hm42sk">
              Quote them
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1015" data-mp="unvrf586awnq" />
            </ButtonLink>
          </div>
        </Card>}

      <Card className="mt-6 grid gap-6 p-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1016" data-mp="13qepzd4xen46">
        <Metric label="Take-home, August" value={currency(month.amount, locale, currencyCode)} detail={`${delta > 0 ? '+' : ''}${delta}% on July`} tone="primary" data-id="element-1017" data-mp="13oapl568k68n" />
        <Metric label="Bookings this month" value={String(month.bookings)} detail={`${chef.bookingsCompleted} lifetime`} data-id="element-1018" data-mp="bwusuiayt34p" />
        <Metric label="Acceptance rate" value={`${chef.acceptanceRate}%`} detail="of requests you take on" data-id="element-1019" data-mp="kraocpfprvmj" />
        <Metric label="Guests who rebook" value={`${chef.repeatRate}%`} detail="within six months" data-id="element-1020" data-mp="g0as7mef5hl3" />
      </Card>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1021" data-mp="3ofko2crptqkf">
        <div className="space-y-8" data-id="element-1022" data-mp="3kfk9ms4m9fdd">
          <section aria-labelledby="next-heading" data-id="element-1023" data-mp="3ke92cibypphi">
            <SectionHeading level={2} title="Your next dates" hint="Confirmed and paid. Tap through for the kitchen notes and dietary record." action={<Link to="/chef/bookings" className="text-[13px] font-medium text-primary underline underline-offset-2" data-id="element-1025" data-mp="awjl2vlr1nio">
                  All bookings
                </Link>} data-id="element-1024" data-mp="z5hvxou6v8b" />
            {confirmed.length ? <ul className="mt-4 space-y-3" data-id="element-1026" data-mp="kmbjk0wyz0sf">
                {confirmed.slice(0, 3).map(booking => <BookingCard key={booking.id} booking={booking} role="chef" data-id="element-1027" data-mp="omn31adliv2b" />)}
              </ul> : <p className="mt-4 rounded-xl border border-dashed border-hairline bg-surface px-4 py-6 text-center text-sm text-ink-2" data-id="element-1028" data-mp="ymm9n1qggbcj">
                Nothing confirmed yet. Quotes you send appear here the moment a guest accepts.
              </p>}
          </section>

          <section aria-labelledby="reply-heading" data-id="element-1029" data-mp="30g3k0d5yzqaz">
            <SectionHeading level={2} title="Waiting on you" hint="Unanswered messages, newest first." data-id="element-1030" data-mp="2svjff8i33s6f" />
            {needsReply.length ? <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface" data-id="element-1031" data-mp="2o1op3m7z4eyd">
                {needsReply.map(thread => {
              const guest = counterpartById(thread.customerId);
              const last = thread.messages[thread.messages.length - 1];
              return <li key={thread.id} data-id="element-1032" data-mp="36tzlqyc1bgo1">
                      <Link to={`/chef/inbox/${thread.id}`} className="flex gap-3 px-4 py-3.5 hover:bg-oat" data-id="element-1033" data-mp="36v0wf1p52158">
                        <Avatar name={guest.name} src={guest.photo} size="sm" data-id="element-1034" data-mp="31x95a9wbij23" />
                        <span className="min-w-0 flex-1" data-id="element-1035" data-mp="2wvjsaopjbvbd">
                          <span className="flex items-baseline justify-between gap-2" data-id="element-1036" data-mp="1qgiwcj0ifd4s">
                            <span className="truncate text-sm font-semibold text-ink" data-id="element-1037" data-mp="1lh67xttzpy03">{guest.name}</span>
                            <span className="shrink-0 text-[11px] text-ink-3" data-id="element-1038" data-mp="dgxtdj5txoqw">{relativeTime(thread.lastAt, locale)}</span>
                          </span>
                          <span className="mt-0.5 block truncate text-[12px] text-ink-3" data-id="element-1039" data-mp="hi9lbvc6em29">{thread.subject}</span>
                          <span className="mt-1 block truncate text-[13px] text-ink-2" data-id="element-1040" data-mp="3le2jb64hg90">{last?.body}</span>
                        </span>
                        <MessageSquareIcon className="mt-1 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" data-id="element-1041" data-mp="3pychb3txemar" />
                      </Link>
                    </li>;
            })}
              </ul> : <p className="mt-4 rounded-xl border border-dashed border-hairline bg-surface px-4 py-6 text-center text-sm text-ink-2" data-id="element-1042" data-mp="3gzr3tn3b9xgu">
                Inbox clear.
              </p>}
          </section>
        </div>

        <aside className="space-y-4" data-id="element-1043" data-mp="37235kn6hy7tx">
          {!!incomplete.length && <Card className="p-5" data-id="element-1044" data-mp="3bylbhqdkcc7u">
              <div className="flex items-center gap-2" data-id="element-1045" data-mp="2sdbc5q7z1hdj">
                <AlertTriangleIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-1046" data-mp="2sdtf15p52tuf" />
                <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1047" data-mp="2od15ebccumxm">Finish your checks</h2>
              </div>
              <ul className="mt-3 space-y-2.5" data-id="element-1048" data-mp="3294k0waqwmoz">
                {incomplete.map(item => <li key={item.kind} className="flex items-start justify-between gap-3" data-id="element-1049" data-mp="2xeh61rddbaon">
                    <span className="min-w-0" data-id="element-1050" data-mp="3bacet4e1z5eb">
                      <span className="block text-sm font-medium text-ink" data-id="element-1051" data-mp="37b3lg0mxd9ja">{item.label}</span>
                      <span className="block text-[12px] text-ink-3" data-id="element-1052" data-mp="3l2i8cijyduvm">{item.detail}</span>
                    </span>
                    <Badge tone={item.status === 'pending' ? 'accent' : 'neutral'} data-id="element-1053" data-mp="3g2m5wnqwmbi6">{item.status}</Badge>
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] text-ink-3" data-id="element-1054" data-mp="3q11w8oi3xgqm">
                Guests can filter search down to fully verified chefs only, so these badges directly affect how often you
                appear.
              </p>
              <ButtonLink to="/chef/studio/profile" variant="outline" size="sm" full className="mt-3" data-id="element-1055" data-mp="2fnaqv3xnrhny">
                Upload documents
              </ButtonLink>
            </Card>}

          <Card className="p-5" data-id="element-1056" data-mp="2plqc4qa07wpj">
            <div className="flex items-center gap-2" data-id="element-1057" data-mp="2pkoyce1lsw44">
              <TrendingUpIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1058" data-mp="1ryhsz1xh6zqp" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1059" data-mp="1rwetzsechw9u">Six months</h2>
            </div>
            <div className="mt-4 flex h-24 items-end gap-1.5" aria-hidden="true" data-id="element-1060" data-mp="21vvy45dy823p">
              {chefEarnings.map((point, i) => <div key={point.month} className="flex flex-1 flex-col items-center gap-1.5" data-id="element-1061" data-mp="26nxh0a5mgr2p" data-mpi={i}>
                  <div className={`w-full rounded-t ${i === chefEarnings.length - 1 ? 'bg-primary' : 'bg-primary/30'}`} style={{
                height: `${point.amount / 248000 * 100}%`
              }} data-id="element-1062" data-mp="196ypyv3gbloo" />
                  <span className="text-[10px] text-ink-3" data-id="element-1063" data-mp="3rerz80kvw5x0">{point.month}</span>
                </div>)}
            </div>
            <ButtonLink to="/chef/earnings" variant="ghost" size="sm" full className="mt-3" data-id="element-1064" data-mp="3ifwrgk26ynrl">
              Earnings & payouts
            </ButtonLink>
          </Card>

          <Card className="p-5" data-id="element-1065" data-mp="3djpjklq8j270">
            <div className="flex items-center gap-2" data-id="element-1066" data-mp="38is1081bc64z">
              <CalendarDaysIcon className="h-4 w-4 text-ink-2" aria-hidden="true" data-id="element-1067" data-mp="38kd0mc2hif9p" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1068" data-mp="33pywnl9raztf">How you are listed</h2>
            </div>
            <dl className="mt-3 space-y-2 text-[13px]" data-id="element-1069" data-mp="2uqu3kugyryon">
              <div className="flex justify-between gap-3" data-id="element-1070" data-mp="2usew6ejpx7jv">
                <dt className="text-ink-2" data-id="element-1071" data-mp="2psackfnijwpf">Booking mode</dt>
                <dd className="font-medium text-ink" data-id="element-1072" data-mp="2ptv7459tvfna">{chef.bookingMode === 'instant' ? 'Instant book' : 'Request to book'}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1073" data-mp="2ytge8ud4qzjm">
                <dt className="text-ink-2" data-id="element-1074" data-mp="2yrww0y7wyef7">Cheapest menu</dt>
                <dd className="tnum font-medium text-ink" data-id="element-1075" data-mp="2tudki62lofqy">
                  from {currency(Math.min(...chef.menus.map(m => m.pricePerGuest)), locale, currencyCode)}
                </dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1076" data-mp="33lhwcwwzbkhk">
                <dt className="text-ink-2" data-id="element-1077" data-mp="2fuvqncag6caq">Minimum order</dt>
                <dd className="tnum font-medium text-ink" data-id="element-1078" data-mp="2fxhy7bxc8rsy">{currency(chef.minimumSpend, locale, currencyCode)}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1079" data-mp="2owlfqbguwz5c">
                <dt className="text-ink-2" data-id="element-1080" data-mp="2otz8kcxvolnn">Deposit</dt>
                <dd className="tnum font-medium text-ink" data-id="element-1081" data-mp="2jwpkp1q50i1x">{chef.depositPercent}%</dd>
              </div>
            </dl>
          </Card>
        </aside>
      </div>
    </AppShell>;
}